import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import TimeSelect from "@/components/subscription/time-selection/TimeSelect";

interface ScheduleSettings {
  deliveryStartTime: string;
  deliveryEndTime: string;
  minimumInterval: number;
  restrictWeekends: boolean;
}

export function ScheduleSettings() {
  const [settings, setSettings] = useState<ScheduleSettings>({
    deliveryStartTime: "09:00",
    deliveryEndTime: "21:00",
    minimumInterval: 30,
    restrictWeekends: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('admin_settings')
          .select('setting_key, setting_value')
          .in('setting_key', [
            'delivery_start_time',
            'delivery_end_time',
            'minimum_delivery_interval',
            'restrict_weekend_delivery'
          ]);

        if (error) throw error;

        const settingsMap = data?.reduce((acc, item) => {
          acc[item.setting_key] = item.setting_value;
          return acc;
        }, {} as Record<string, any>);

        setSettings({
          deliveryStartTime: settingsMap?.delivery_start_time || "09:00",
          deliveryEndTime: settingsMap?.delivery_end_time || "21:00",
          minimumInterval: settingsMap?.minimum_delivery_interval || 30,
          restrictWeekends: settingsMap?.restrict_weekend_delivery || false
        });
      } catch (error) {
        console.error("Error fetching schedule settings:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load schedule settings"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates = [
        {
          setting_key: 'delivery_start_time',
          setting_value: settings.deliveryStartTime,
          description: 'Daily start time for quote delivery'
        },
        {
          setting_key: 'delivery_end_time',
          setting_value: settings.deliveryEndTime,
          description: 'Daily end time for quote delivery'
        },
        {
          setting_key: 'minimum_delivery_interval',
          setting_value: settings.minimumInterval,
          description: 'Minimum minutes between quote deliveries'
        },
        {
          setting_key: 'restrict_weekend_delivery',
          setting_value: settings.restrictWeekends,
          description: 'Whether to restrict quote delivery on weekends'
        }
      ];

      const { error } = await supabase
        .from('admin_settings')
        .upsert(updates, { onConflict: 'setting_key' });

      if (error) throw error;

      toast({
        title: "Settings Updated",
        description: "Schedule settings have been updated successfully"
      });
    } catch (error) {
      console.error("Error saving schedule settings:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save schedule settings"
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Delivery Schedule</CardTitle>
          <CardDescription>
            Configure when quotes can be delivered to subscribers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Time</Label>
              <TimeSelect
                hours={settings.deliveryStartTime.split(":")[0]}
                minutes={settings.deliveryStartTime.split(":")[1]}
                onChange={(hours, minutes) => 
                  setSettings(s => ({
                    ...s,
                    deliveryStartTime: `${hours}:${minutes}`
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <TimeSelect
                hours={settings.deliveryEndTime.split(":")[0]}
                minutes={settings.deliveryEndTime.split(":")[1]}
                onChange={(hours, minutes) => 
                  setSettings(s => ({
                    ...s,
                    deliveryEndTime: `${hours}:${minutes}`
                  }))
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Minimum Interval (minutes)</Label>
            <Input
              type="number"
              min="5"
              max="1440"
              value={settings.minimumInterval}
              onChange={(e) => 
                setSettings(s => ({
                  ...s,
                  minimumInterval: parseInt(e.target.value) || 30
                }))
              }
            />
            <p className="text-sm text-muted-foreground">
              Minimum time between quote deliveries for each subscriber
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Restrict Weekend Delivery</Label>
              <p className="text-sm text-muted-foreground">
                Disable quote delivery on weekends
              </p>
            </div>
            <Switch
              checked={settings.restrictWeekends}
              onCheckedChange={(checked) =>
                setSettings(s => ({ ...s, restrictWeekends: checked }))
              }
            />
          </div>

          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
} 